"use client";

import { TFunction } from "i18next";
import { SupportedLanguages } from "@/enums/internationalization";
import { VoiceOption } from "@/types/agencies";
import {
  groupVoicesByLanguage,
  type VoiceLanguageGroup,
} from "@/utils/scripts/groupVoicesByLanguage";
import VoiceCardGrid from "./VoiceCardGrid";

interface VoiceLanguageCatalogProps {
  voices: readonly VoiceOption[];
  /** Org-enabled languages; section headings are scoped to these when set. */
  orgLanguages?: readonly SupportedLanguages[];
  selectedVoiceId: string | undefined;
  onVoiceSelect: (voice: VoiceOption) => void;
  changeMarker: string;
  setPlaying: (audioId: string) => void;
  translateInbound: TFunction;
  translateCommon: TFunction;
  translateSettings: TFunction;
  orgId?: string;
}

function groupHeading(
  group: VoiceLanguageGroup,
  translateInbound: TFunction,
  translateCommon: TFunction
): string {
  if (group.languages.length === 0) {
    return translateInbound("pages.agent.otherVoices");
  }
  return group.languages.map((lang) => translateCommon(lang)).join(" & ");
}

export default function VoiceLanguageCatalog({
  voices,
  orgLanguages,
  selectedVoiceId,
  onVoiceSelect,
  changeMarker,
  setPlaying,
  translateInbound,
  translateCommon,
  translateSettings,
  orgId,
}: Readonly<VoiceLanguageCatalogProps>) {
  const groups = groupVoicesByLanguage(voices, orgLanguages);

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <section key={group.key} className="space-y-2">
          <h3 className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
            {groupHeading(group, translateInbound, translateCommon)}
          </h3>
          <VoiceCardGrid
            voices={group.voices}
            selectedVoiceId={selectedVoiceId}
            onVoiceSelect={onVoiceSelect}
            changeMarker={changeMarker}
            setPlaying={setPlaying}
            translateCommon={translateCommon}
            translateSettings={translateSettings}
            orgId={orgId}
          />
        </section>
      ))}
    </div>
  );
}
